import { Archive, Camera, CreditCard, Edit, File, Search, Trash2, Users } from "lucide-react";
import EmployeeDialogSection from "./EmployeeDialogSection";

const EmployeesSection = ({ ctx }) => {
  const {
    categoryFilter,
    filteredEmployees,
    getProjectName,
    handleDeleteEmployee,
    handleDownloadIdCard,
    handleEditEmployee,
    openArchiveDialog,
    openDocumentDialog,
    openPhotoDialog,
    projectFilter,
    projects,
    searchTerm,
    setCategoryFilter,
    setProjectFilter,
    setSearchTerm,
    setStatusFilter,
    statusFilter
  } = ctx;
  return (
    <>
        <div>
          {/* Actions Bar */}
          <div className="flex flex-wrap gap-4 mb-6">
            <div className="flex-1 min-w-[200px]">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <input
                  type="text"
                  placeholder="Search by name, code, designation..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  data-testid="employee-search"
                  className="w-full pl-10 pr-4 py-2 border border-slate-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-primary"
                />
              </div>
            </div>
            <select
              value={projectFilter}
              onChange={(e) => setProjectFilter(e.target.value)}
              data-testid="project-filter"
              className="px-4 py-2 border border-slate-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-primary"
            >
              <option value="">All Projects</option>
              <option value="unassigned">Unassigned</option>
              {projects.map(project => (
                <option key={project.id} value={project.id}>
                  {project.name?.substring(0, 30)}{project.name?.length > 30 ? '...' : ''}
                </option>
              ))}
            </select>
            <select
              value={categoryFilter}
              onChange={(e) => setCategoryFilter(e.target.value)}
              data-testid="category-filter"
              className="px-4 py-2 border border-slate-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-primary"
            >
              <option value="">All Categories</option>
              <option value="Skilled">Skilled</option>
              <option value="Semi-skilled">Semi-skilled</option>
              <option value="Unskilled">Unskilled</option>
              <option value="Supervisor">Supervisor</option>
              <option value="Staff">Staff</option>
            </select>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              data-testid="status-filter"
              className="px-4 py-2 border border-slate-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-primary"
            >
              <option value="">All Status</option>
              <option value="Active">Active</option>
              <option value="Inactive">Inactive</option>
            </select>
            <EmployeeDialogSection ctx={ctx} />
          </div>

          {/* Employees Table */}
          <div className="stat-card overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b-2 border-slate-300">
                  <th className="text-left py-3 px-4 text-xs uppercase tracking-widest text-slate-700 font-medium">Code</th>
                  <th className="text-left py-3 px-4 text-xs uppercase tracking-widest text-slate-700 font-medium">Name</th>
                  <th className="text-left py-3 px-4 text-xs uppercase tracking-widest text-slate-700 font-medium">Designation</th>
                  <th className="text-left py-3 px-4 text-xs uppercase tracking-widest text-slate-700 font-medium">Category</th>
                  <th className="text-left py-3 px-4 text-xs uppercase tracking-widest text-slate-700 font-medium">Project</th>
                  <th className="text-left py-3 px-4 text-xs uppercase tracking-widest text-slate-700 font-medium">Type</th>
                  <th className="text-center py-3 px-4 text-xs uppercase tracking-widest text-slate-700 font-medium">Status</th>
                  <th className="text-center py-3 px-4 text-xs uppercase tracking-widest text-slate-700 font-medium">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredEmployees.map((emp) => (
                  <tr key={emp.id} className="border-b border-slate-200 hover:bg-slate-50" data-testid={`employee-row-${emp.id}`}>
                    <td className="py-3 px-4 font-mono text-sm font-medium text-primary">{emp.employee_code}</td>
                    <td className="py-3 px-4">
                      <div className="flex items-center space-x-3">
                        {emp.photo_url ? (
                          <img 
                            src={`${process.env.REACT_APP_BACKEND_URL}${emp.photo_url}`}
                            alt={emp.name}
                            className="w-8 h-8 rounded-full object-cover border border-slate-200"
                          />
                        ) : (
                          <div className="w-8 h-8 rounded-full bg-slate-200 flex items-center justify-center">
                            <Users className="w-4 h-4 text-slate-400" />
                          </div>
                        )}
                        <div>
                          <p className="font-medium text-sm">{emp.name}</p>
                          <p className="text-xs text-slate-500">{emp.phone}</p>
                        </div>
                      </div>
                    </td>
                    <td className="py-3 px-4 text-sm">{emp.designation}</td>
                    <td className="py-3 px-4">
                      <span className={`px-2 py-1 rounded-sm text-xs font-medium ${
                        emp.category === 'Skilled' ? 'bg-primary/10 text-primary' :
                        emp.category === 'Semi-skilled' ? 'bg-accent/10 text-accent' :
                        emp.category === 'Supervisor' ? 'bg-success/10 text-success' :
                        emp.category === 'Staff' ? 'bg-warning/10 text-warning' :
                        'bg-slate-100 text-slate-600'
                      }`}>
                        {emp.category}
                      </span>
                    </td>
                    <td className="py-3 px-4">
                      <span className={`px-2 py-1 rounded-sm text-xs font-medium truncate max-w-[150px] inline-block ${
                        emp.assigned_project_id ? 'bg-blue-50 text-blue-700' : 'bg-slate-100 text-slate-500'
                      }`} title={getProjectName(emp.assigned_project_id)}>
                        {getProjectName(emp.assigned_project_id).substring(0, 20)}{getProjectName(emp.assigned_project_id).length > 20 ? '...' : ''}
                      </span>
                    </td>
                    <td className="py-3 px-4 text-sm">{emp.employment_type}</td>
                    <td className="py-3 px-4 text-center">
                      <span className={`px-2 py-1 rounded-sm text-xs font-medium ${
                        emp.status === 'Active' ? 'bg-success/20 text-success' : 'bg-slate-200 text-slate-600'
                      }`}>
                        {emp.status}
                      </span>
                    </td>
                    <td className="py-3 px-4 text-center">
                      <div className="flex items-center justify-center space-x-1">
                        <button
                          onClick={() => openPhotoDialog(emp)}
                          data-testid={`photo-employee-${emp.id}`}
                          className="p-1 hover:bg-slate-200 rounded"
                          title="Upload Photo"
                        >
                          <Camera className="w-4 h-4 text-primary" />
                        </button>
                        <button
                          onClick={() => handleDownloadIdCard(emp)}
                          data-testid={`idcard-employee-${emp.id}`}
                          className="p-1 hover:bg-slate-200 rounded"
                          title="Download ID Card"
                        >
                          <CreditCard className="w-4 h-4 text-accent" />
                        </button>
                        <button
                          onClick={() => openDocumentDialog(emp)}
                          data-testid={`docs-employee-${emp.id}`}
                          className="p-1 hover:bg-slate-200 rounded"
                          title="Documents"
                        >
                          <File className="w-4 h-4 text-slate-500" />
                        </button>
                        <button
                          onClick={() => handleEditEmployee(emp)}
                          data-testid={`edit-employee-${emp.id}`}
                          className="p-1 hover:bg-slate-200 rounded"
                          title="Edit"
                        >
                          <Edit className="w-4 h-4 text-primary" />
                        </button>
                        <button
                          onClick={() => openArchiveDialog(emp)}
                          data-testid={`archive-employee-${emp.id}`}
                          className="p-1 hover:bg-amber-100 rounded"
                          title="Archive (Remove)"
                        >
                          <Archive className="w-4 h-4 text-amber-600" />
                        </button>
                        <button
                          onClick={() => handleDeleteEmployee(emp.id, emp.name)}
                          data-testid={`delete-employee-${emp.id}`}
                          className="p-1 hover:bg-red-100 rounded"
                          title="Deactivate"
                        >
                          <Trash2 className="w-4 h-4 text-error" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {filteredEmployees.length === 0 && (
              <div className="text-center py-12 text-slate-500">
                <Users className="w-16 h-16 mx-auto mb-4 text-slate-400" />
                {searchTerm || categoryFilter || statusFilter 
                  ? "No employees match your filters" 
                  : "No employees added yet. Click 'Add Employee' to get started."}
              </div>
            )}
          </div>
        </div>
    </>
  );
};

export default EmployeesSection;
